import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "../../context/SiteContentContext";
import { createOrder } from "../../services/newspaperApi";
import { clearBasket, readBasket, readLastOrder, removeFromBasket, saveLastOrder } from "../../services/noticeBasket";
import PaymentBox from "./PaymentBox";
import ContactAdminBox from "./ContactAdminBox";
import "./newspaper.css";

// /newspaper/checkout (owner 28-10-2569): every notice the customer sent from this browser → printed copies → one
// total → payment (QR / bank + slip) and the contact-admin box. The server checks the notices and computes the total.
const baht = (n) => `${Number(n || 0).toLocaleString("th-TH")} บาท`;
const MAX_COPIES = 50;

function OrderDone({ order }) {
  return (
    <div className="submission-success">
      <span>✓</span>
      <h2>ส่งรายการชำระเงินแล้ว</h2>
      <p>
        เลขรายการชำระเงิน <strong>{order.orderCode}</strong> · {order.count} เรื่อง
        {order.copies ? ` · หนังสือพิมพ์ฉบับพิมพ์ ${order.copies} เล่ม` : ""}
      </p>
      <div className="basket-box">
        <ol>
          {order.items.map((i) => <li key={i.trackingCode}><strong>{i.trackingCode}</strong> · {i.title}</li>)}
        </ol>
        <p className="basket-total">ยอดที่ต้องชำระ <strong>{baht(order.total)}</strong></p>
      </div>
      <PaymentBox trackingCode={order.orderCode} phone={order.phone || ""} amount={order.total} />
      <ContactAdminBox trackingCode={order.orderCode} status="submitted" />
      <Link className="button outline" to={`/newspaper/status?code=${encodeURIComponent(order.orderCode)}`}>
        ติดตามสถานะประกาศ{" "}
        <span>↗</span>
      </Link>
    </div>
  );
}

function CheckoutPage() {
  const { siteContent } = useSiteContent();
  const p = siteContent.paymentInfo || {};
  const priceNotice = Number(p.pricePerNotice ?? 50) || 0;
  const priceCopy = Number(p.pricePerCopy ?? 150) || 0;
  const [basket, setBasket] = useState(readBasket);
  const [phone, setPhone] = useState(() => readBasket().contact.phone || "");
  const [copies, setCopies] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const lastOrder = readLastOrder();

  useEffect(() => {
    document.title = "ชำระค่าลงประกาศ | Maneenopkao Group";
  }, []);

  const items = basket.items;
  const total = items.length * priceNotice + copies * priceCopy;

  const confirm = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await createOrder(items.map((i) => i.trackingCode), phone.trim(), copies);
      saveLastOrder(res.order, phone.trim());
      clearBasket();
      setOrder({ ...res.order, phone: phone.trim() });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main" className="container submit-page checkout-page">
      <Link className="breadcrumb" to="/newspaper/submit">← ลงประกาศเพิ่ม</Link>
      <p className="eyebrow">CHECKOUT</p>
      <h1>ชำระค่าลงประกาศ</h1>

      {order ? (
        <OrderDone order={order} />
      ) : items.length === 0 ? (
        <div className="basket-box">
          <p>ยังไม่มีประกาศที่รอชำระเงินในเครื่องนี้</p>
          <div className="basket-actions">
            <Link className="button dark" to="/newspaper/submit">ลงประกาศ <span>→</span></Link>
            {lastOrder?.orderCode && (
              <Link className="button outline" to={`/newspaper/status?code=${encodeURIComponent(lastOrder.orderCode)}`}>
                ดูรายการชำระเงินล่าสุด ({lastOrder.orderCode})
              </Link>
            )}
          </div>
        </div>
      ) : (
        <form className="site-form basket-box" onSubmit={confirm}>
          <p className="eyebrow">ประกาศที่รอชำระเงิน · {items.length} เรื่อง</p>
          <table className="basket-table">
            <tbody>
              {items.map((i) => (
                <tr key={i.trackingCode}>
                  <td><strong>{i.trackingCode}</strong> · {i.title}</td>
                  <td className="num">{baht(priceNotice)}</td>
                  <td>
                    <button type="button" className="text-link" onClick={() => setBasket(removeFromBasket(i.trackingCode))} aria-label={`เอา ${i.trackingCode} ออกจากรายการ`}>
                      เอาออก
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <label className="basket-copies">
                    หนังสือพิมพ์ฉบับพิมพ์ (เล่มจริง) เล่มละ {baht(priceCopy)}
                    <input
                      type="number"
                      min="0"
                      max={MAX_COPIES}
                      value={copies}
                      onChange={(e) => setCopies(Math.max(0, Math.min(MAX_COPIES, Math.floor(Number(e.target.value) || 0))))}
                    />
                  </label>
                </td>
                <td className="num">{baht(copies * priceCopy)}</td>
                <td />
              </tr>
            </tbody>
          </table>
          <p className="basket-total">ยอดรวมที่ต้องชำระ <strong>{baht(total)}</strong></p>
          <label>
            เบอร์โทรที่ใช้ตอนส่งประกาศ <span className="required">*</span>
            <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="basket-actions">
            <Link className="button outline" to="/newspaper/submit">+ ลงประกาศเรื่องถัดไป</Link>
            <button className="button dark" type="submit" disabled={busy}>
              {busy ? "กำลังสร้างรายการ..." : `ยืนยันรายการ และไปชำระเงิน (${baht(total)})`}{" "}
              <span>→</span>
            </button>
          </div>
          <p className="adm-note">ประกาศที่เอาออกจากรายการยังอยู่ในระบบ ชำระภายหลังได้ที่หน้าติดตามสถานะ</p>
        </form>
      )}
    </main>
  );
}

export default CheckoutPage;
